/* ==========================================================================
   Rook Components — Art Calculator (faixas)
   Web Component: <rk-art-calculator-element>
   Hidratação 'idle'.

   Calcula quantas "faixas" são necessárias a partir da largura da parede:
     faixas = ceil( (largura + sangria) / largura_da_faixa ), mínimo 1
   e:
     - mostra "São necessárias N faixas";
     - define a quantidade (= faixas) no(s) product-form(s); se o form não tiver
       input[name="quantity"] (stepper removido/escondido), injeta um hidden;
     - injeta properties[Largura|Altura|Faixas];
     - reforça no submit (captura) no FORM que está sendo enviado — imune a
       múltiplos forms na página e a re-render por troca de variante;
     - emite rk:faixa:update { faixas } p/ os blocos de preço reativos.

   Compõe o product-form nativo sem modificá-lo.
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkArtCalculator extends RkComponent {
  static hydration = 'idle';

  setup() {
    this.faixaWidth = parseFloat(this.dataset.faixaWidth) || 60;
    this.sangria = parseFloat(this.dataset.sangria) || 0;
    this.sendProperties = this.dataset.sendProperties !== 'false';
    this.hideQty = this.dataset.hideQuantitySelector !== 'false';
    this.resultTemplate = this.dataset.resultTemplate || 'São necessárias {n} faixas';

    this.widthInput = this.querySelector('[data-role="width"]');
    this.heightInput = this.querySelector('[data-role="height"]');
    this.resultEl = this.querySelector('[ref="result"]');

    if (this.hideQty) this.#hideQuantitySelector();

    this.delegate({
      'input [data-role]': this.onChange,
    });

    // Reforço no submit do FORM que está sendo enviado (captura → roda antes
    // do handleSubmit do tema, que lê new FormData(form)).
    this.onFormSubmit = this.onFormSubmit.bind(this);
    document.addEventListener('submit', this.onFormSubmit, true);

    this.onChange();
  }

  teardown() {
    document.removeEventListener('submit', this.onFormSubmit, true);
    document.querySelectorAll('input[data-rk-art]').forEach((el) => el.remove());
  }

  /** @returns {HTMLFormElement[]} */
  #allForms() {
    const forms = [];
    document.querySelectorAll('product-form-component').forEach((pf) => {
      const form = pf.querySelector('form');
      if (form) forms.push(form);
    });
    return forms;
  }

  #hideQuantitySelector() {
    document.querySelectorAll('product-form-component').forEach((pf) => {
      const wrap = pf.querySelector('.quantity-selector-wrapper');
      if (wrap) wrap.hidden = true;
      const label = pf.querySelector('.quantity-label');
      if (label) label.hidden = true;
    });
  }

  get widthValue() {
    return parseFloat(this.widthInput?.value) || 0;
  }

  get heightValue() {
    return parseFloat(this.heightInput?.value) || 0;
  }

  computeFaixas() {
    const w = this.widthValue;
    if (w <= 0) return 0;
    return Math.max(1, Math.ceil((w + this.sangria) / this.faixaWidth));
  }

  onChange() {
    const faixas = this.computeFaixas();

    if (this.resultEl) {
      this.resultEl.textContent =
        faixas > 0 ? this.resultTemplate.replace('{n}', String(faixas)) : '';
      this.resultEl.toggleAttribute('hidden', faixas <= 0);
    }

    if (faixas <= 0) return;

    this.#allForms().forEach((form) => this.#applyToForm(form, faixas));
    this.rkEmit('faixa:update', { faixas });
  }

  /** @param {Event} event */
  onFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('product-form-component')) return;

    const faixas = this.computeFaixas();
    if (faixas <= 0) return;

    this.#applyToForm(form, faixas);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {number} faixas
   */
  #applyToForm(form, faixas) {
    let qty = form.querySelector('input[name="quantity"]');
    if (!qty) {
      qty = document.createElement('input');
      qty.type = 'hidden';
      qty.name = 'quantity';
      qty.setAttribute('data-rk-art', '');
      form.appendChild(qty);
    }
    qty.value = String(faixas);

    if (this.sendProperties) {
      this.#prop(form, 'Largura', this.widthValue > 0 ? `${this.widthValue} cm` : '');
      this.#prop(form, 'Altura', this.heightValue > 0 ? `${this.heightValue} cm` : '');
      this.#prop(form, 'Faixas', String(faixas));
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #prop(form, name, value) {
    const selector = `input[name="properties[${name}]"][data-rk-art]`;
    let input = form.querySelector(selector);

    if (value === '') {
      if (input) input.remove();
      return;
    }

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.setAttribute('data-rk-art', '');
      form.appendChild(input);
    }
    input.value = value;
  }
}

if (!customElements.get('rk-art-calculator-element')) {
  customElements.define('rk-art-calculator-element', RkArtCalculator);
}
